import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { ReactElement, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useStoreState } from '~/store/hooks';
import useRole from '~/store/user/hooks/useRole';

import { mergeAllocationByUserId } from '~/utils/clientAllocation';
import extractDateFromString from '~/utils/date/extractDateFromString';
import getIsGlobalUser from '~/utils/roles/getIsGlobalUser';
import PrivateImage from '~/ui/components/common/PrivateImage';
import { ReactComponent as GlobeIcon } from '~/ui/assets/images/globe.svg';
import smallAvatar from '~/ui/assets/images/smallAvatar.svg';

import EditIcon from '~/ui/assets/images/editWhiteSecond.svg';
import Breadcrumbs from '~/ui/components/common/Breadcrumbs';
import Loader from '~/ui/components/common/Loader';
import { DUPLICATE_CLIENT_ALLOCATION, EDIT_CLIENT_ALLOCATION, NETWORK } from '~/ui/constants/paths';

import useGetClientAllocations from '~/ui/pages/ActTeam/hooks/useGetClientAllocations';
import groupAllocationClients from './helpers/groupAllocationClients';
import ClientCell from './components/ClientsCell';
import LocationGroupClient from './components/LocationGroupClient';

import variables from '~/ui/assets/styles/colors.module.scss';

import styles from './ViewClientAllocation.module.scss';

type IParams = {
  actTeamId: string;
  clientAllocationDate: string;
};

const ViewClientAllocation = (): ReactElement => {
  const { actTeamId, clientAllocationDate } = useParams<IParams>();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { clinic: userClinic } = useStoreState(state => state.user.current);
  const actTeam = useStoreState(state => state.actTeam.current);
  const { isArchived } = actTeam || {};

  const { getClientAllocations, loading, clientAllocations, locationGroups } =
    useGetClientAllocations();

  const { isActTeam, isActTeamMember, isGlobalUser } = useRole();

  const hasOwnAllocation = clientAllocations?.some(item => item.isOwn && !item.isArchived);

  useEffect(() => {
    const allocationDate = `${clientAllocationDate}T00:00:00Z`;

    getClientAllocations({ actTeamId, allocationDate });
  }, [getClientAllocations, actTeamId, clientAllocationDate]);

  const filteredAllocations = useMemo(
    () => (clientAllocations || []).filter(a => !a.isArchived),
    [clientAllocations],
  );

  const allAllocationsAreResolved = useMemo(
    () => filteredAllocations.every(item => !!item.resolved),
    [filteredAllocations],
  );

  if (loading || (!actTeam && !isGlobalUser) || !clientAllocations) return <Loader />;

  const breadcrumbItems = [
    ...(isGlobalUser ? [{ to: NETWORK, title: `${userClinic.name} Teams` }] : []),
    ...(isActTeam
      ? [
          {
            title: actTeam.name,
            to: {
              pathname: NETWORK,
            },
          },
        ]
      : []),
    ...(!isActTeam && !isGlobalUser ? [{ to: NETWORK, title: `${userClinic.name} Teams` }] : []),
    { to: pathname, title: 'Client Allocation' },
  ];

  return (
    <div>
      <div className={styles.header}>
        <div>
          <h2>{extractDateFromString(clientAllocations[0]?.date)}</h2>
          <Breadcrumbs itemsForBreadcrumbs={breadcrumbItems} />
        </div>
        {!isArchived && !allAllocationsAreResolved && (
          <>
            <div>
              <Button
                color="primary"
                variant="outlined"
                className={styles.buttonWrapper}
                disabled={(isActTeamMember || isGlobalUser) && !hasOwnAllocation}
                onClick={() =>
                  navigate(
                    DUPLICATE_CLIENT_ALLOCATION.replace(':actTeamId', actTeamId).replace(
                      ':clientAllocationDate',
                      String(clientAllocations[0].date.split('T')[0]),
                    ),
                  )
                }
              >
                Duplicate
              </Button>
              <Button
                color="primary"
                variant="contained"
                startIcon={<img src={EditIcon} alt="edit" />}
                disabled={(isActTeamMember || isGlobalUser) && !hasOwnAllocation}
                onClick={() =>
                  navigate(
                    EDIT_CLIENT_ALLOCATION.replace(':actTeamId', actTeamId).replace(
                      ':clientAllocationDate',
                      String(clientAllocations[0].date.split('T')[0]),
                    ),
                  )
                }
              >
                Edit Client Allocation
              </Button>
            </div>
          </>
        )}
      </div>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Team Members</TableCell>
              <TableCell>Clients</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {mergeAllocationByUserId(filteredAllocations).map(allocation => {
              if (allocation.isArchived) {
                return null;
              }

              const allocationClients = groupAllocationClients(
                locationGroups,
                allocation.user.clients,
              );

              const hasGlobalUserRole = getIsGlobalUser(allocation.user.roles, userClinic?.id);

              return (
                <TableRow key={allocation.id}>
                  <TableCell>
                    <Box className={styles.nameCell}>
                      <div className={styles.photoContainer}>
                        <PrivateImage
                          className={styles.image}
                          src={allocation.user?.photo || smallAvatar}
                          alt="avatar"
                        />
                        {hasGlobalUserRole && (
                          <div className={styles.absoluteIcon}>
                            <GlobeIcon width={14} height={14} color={variables.colorGreenGlobal} />
                          </div>
                        )}
                      </div>
                      <Typography className={styles.name}>{allocation.user.name}</Typography>
                      <Typography variant="body2" className={styles.subRoles}>
                        {allocation.user.subRoles.map(subRole => subRole.name).join(', ')}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {!!allocation.crisisClients?.length && (
                      <ClientCell clients={allocation.crisisClients} isDispatch />
                    )}
                    {allocation.locationGroups?.length ? (
                      <LocationGroupClient
                        groups={allocationClients.filter(group => group.clients?.length)}
                        allocationId={allocation.id}
                      />
                    ) : (
                      !!allocation.user.clients.length && (
                        <ClientCell clients={allocation.user.clients} isDispatch={false} />
                      )
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default ViewClientAllocation;
